import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  PlayArrow as ExecuteIcon,
} from '@mui/icons-material';
import { PageListLayout } from '../components/layouts/PageListLayout';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { agentFunctionService } from '../services/agentFunctionService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';
import type { AgentFunctionDto, AgentFunctionFilters } from '../types/agentFunction';

/**
 * Helper to build dynamic agent function routes.
 */
const agentFunctionUrl = (base: string, id: string) => base.replace(':id', id);

/**
 * Agent Functions list page component.
 */
export const AgentFunctionsListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);

  const [functions, setFunctions] = useState<AgentFunctionDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<AgentFunctionFilters>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await agentFunctionService.getAgentFunctions(filters);
      setFunctions(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load agent functions';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, addNotification, currentTenant?.id, currentProject?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    try {
      await agentFunctionService.deleteAgentFunction(deleteId);
      addNotification('Agent function deleted successfully', 'success');
      setDeleteId(null);
      await loadData();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete agent function', 'error');
    }
  };

  const tableColumns: DataTableColumn<AgentFunctionDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (row) => (
        <Typography variant="body2" fontWeight={500}>
          {row.name}
        </Typography>
      ),
    },
    {
      id: 'slug',
      label: 'Slug',
      minWidth: 180,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.slug}
        </Typography>
      ),
    },
    {
      id: 'agent',
      label: 'Agent',
      minWidth: 180,
      render: (row) => (
        <Typography variant="body2">{row.agentDefinitionName}</Typography>
      ),
    },
    {
      id: 'isActive',
      label: 'Active',
      minWidth: 100,
      render: (row) => (
        <Chip
          label={row.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={row.isActive ? 'success' : 'default'}
          variant={row.isActive ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'requestCount',
      label: 'Requests',
      minWidth: 100,
      render: (row) => (
        <Typography variant="body2">{row.requestCount}</Typography>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created At',
      minWidth: 160,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 200,
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View Detail">
            <IconButton
              size="small"
              onClick={() => navigate(agentFunctionUrl(ROUTES.AGENT_FUNCTION_DETAIL, row.id))}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Execute">
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                navigate(`${ROUTES.AGENT_FUNCTION_EXECUTE}?slug=${encodeURIComponent(row.slug)}`)
              }
              disabled={!row.isActive}
            >
              <ExecuteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => navigate(agentFunctionUrl(ROUTES.AGENT_FUNCTION_EDIT, row.id))}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View History">
            <IconButton
              size="small"
              onClick={() => navigate(agentFunctionUrl(ROUTES.AGENT_FUNCTION_REQUESTS, row.id))}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteId(row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <PageListLayout
        title="Agent Functions"
        createButton={{
          label: 'Create Agent Function',
          onClick: () => navigate(ROUTES.AGENT_FUNCTION_NEW),
          icon: <AddIcon />,
        }}
        filters={
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              label="Search"
              size="small"
              value={filters.searchTerm || ''}
              onChange={(e) =>
                setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 })
              }
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Active</InputLabel>
              <Select
                value={
                  filters.isActive === undefined
                    ? ''
                    : filters.isActive
                    ? 'active'
                    : 'inactive'
                }
                label="Active"
                onChange={(e) => {
                  const value = e.target.value as string;
                  setFilters({
                    ...filters,
                    isActive: value === '' ? undefined : value === 'active',
                    pageNumber: 1,
                  });
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        }
        loading={loading}
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        isEmpty={functions.length === 0}
        emptyState={{
          title: 'No agent functions found',
          description:
            'Try adjusting your filters or create a new agent function to get started.',
          action: {
            label: 'Create Agent Function',
            onClick: () => navigate(ROUTES.AGENT_FUNCTION_NEW),
          },
        }}
        pagination={{
          page: filters.pageNumber || 1,
          totalPages: Math.ceil(totalCount / (filters.pageSize || 10)),
          onChange: (page) => setFilters({ ...filters, pageNumber: page }),
        }}
      >
        <DataTable columns={tableColumns} rows={functions} loading={false} />
      </PageListLayout>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Agent Function</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this agent function? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
